/**
 * Everything this app reads from its environment, in one place.
 *
 * `@hb/config` is server-side: it validates a process environment at boot and
 * would pull `process.env` into a browser bundle. Vite substitutes
 * `import.meta.env.VITE_*` at build time instead, so these values are baked
 * into the artefact and are public. The rule the two share is the one that
 * matters: nothing reads the environment except this file.
 */

/**
 * Origin of the API.
 *
 * The default is `api.hb.localhost` rather than plain `localhost` on purpose:
 * the single-sign-on refresh cookie is set on `.hb.localhost`, and a browser
 * will not send a domain cookie to a host outside that domain. Anything under
 * `.localhost` resolves to the loopback address with no hosts-file entry, so
 * this works out of the box - open the app at `http://id.hb.localhost:5174`.
 */
export const API_URL = import.meta.env.VITE_API_URL ?? 'http://api.hb.localhost:3000';

/**
 * Origin of the chat SPA.
 *
 * Identity is where every session begins, so it is also where somebody ends up
 * after signing in, verifying an address or changing a password - and without a
 * way onward from here, the only route to the application they actually came for
 * is a bookmark. Chat already links back the other way; this is the other half.
 *
 * A full origin rather than a path, because it is a different one: the session
 * crosses on the refresh cookie set on `.hb.localhost`, which is the same
 * mechanism that carries it from here to anywhere else on the domain.
 */
export const CHAT_URL = import.meta.env.VITE_CHAT_URL ?? 'http://chat.hb.localhost:5175';

/**
 * Origin of the photos SPA.
 *
 * The other half of the same idea as {@link CHAT_URL}, and it exists now for the
 * reason it did not before: there is a photos application to go to.
 */
export const PHOTOS_URL = import.meta.env.VITE_PHOTOS_URL ?? 'http://photos.hb.localhost:5176';

/**
 * Where somebody writes when they cannot sign in to delete their account - the
 * password and the second factor both lost. The public deletion page names it,
 * and a store listing points at that page, so it has to be a mailbox somebody
 * reads. Not from the environment: it is a fact about the service, the same in
 * every build.
 */
export const PRIVACY_ADDRESS = 'privacy@honore-bagni.fr';
